import { matchPath } from 'react-router-dom'
import { Box, Divider, List } from '@mui/material'
import React from 'react'

import NavItem from './NavItem'

interface SidebarItem {
  title: string
  path: string
  icon: React.ReactElement
  info?: React.ReactNode
  childItems?: SidebarItem[]
}

interface RenderNavItemsArgs {
  depth?: number
  items: SidebarItem[]
  pathname: string
  collapsed: boolean
}

const renderNavItems = ({ depth = 0, items, pathname, collapsed }: RenderNavItemsArgs) => (
  <List disablePadding>
    {items.reduce<React.ReactElement[]>((acc, item) => reduceChildRoutes({ acc, item, pathname, depth, collapsed }), [])}
  </List>
)

interface ReduceChildRoutesArgs {
  acc: React.ReactElement[]
  pathname: string
  item: SidebarItem
  depth: number
  collapsed: boolean
}

const reduceChildRoutes = ({ acc, pathname, item, depth, collapsed }: ReduceChildRoutesArgs) => {
  const key = `${item.title}-${depth}`
  const baseButtonStyle = { borderRadius: '0.5rem' }
  const buttonStyle = collapsed
    ? { ...baseButtonStyle, display: 'inline', justifyContent: 'center' }
    : { ...baseButtonStyle, width: '100%' }

  const prefixMatch = item.path ? !!matchPath({ path: item.path, end: false }, pathname) : false

  if (item.childItems && item.childItems.length > 0) {
    const partialMatch = item.path ? !!matchPath({ path: item.path, end: false }, pathname) : false

    acc.push(
      <NavItem
        active={partialMatch}
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={partialMatch}
        path={item.path}
        title={item.title}
        childItems={item.childItems}
        style={buttonStyle}
        collapsed={collapsed}
      >
        {renderNavItems({ depth: depth + 1, items: item.childItems, pathname, collapsed })}
      </NavItem>
    )
  } else {
    acc.push(
      <NavItem
        active={prefixMatch}
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        path={item.path}
        title={item.title}
        collapsed={collapsed}
      />
    )
  }

  return acc
}

interface NavSectionProps {
  items: SidebarItem[]
  pathname: string
  title?: string
  collapsed?: boolean
  [key: string]: any
}

const NavSection = ({ items, pathname, collapsed = false, ...other }: NavSectionProps) => {
  return (
    <Box display="flex" textAlign="center" flexDirection="column">
      <Divider />
      <List {...other} sx={{ width: '100%' }}>
        {renderNavItems({ items, pathname, collapsed })}
      </List>
    </Box>
  )
}

export default NavSection
